// One AI form: tabs on the left, the live preview on the right. Edits save
// themselves (2 s after typing stops, one save at a time, PATCH of the whole
// draft); Publish makes them live. Ported from CCP-V2 FormEditorPage.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { I } from '../../../common/v5/config';
import {
	deleteForm,
	getEmbed,
	getForm,
	hostedLink,
	listAreas,
	previewToken as fetchPreviewToken,
	publishForm,
	rotateKey,
	unpublishForm,
	updateForm
} from './api';
import {
	LANGUAGE_NAMES,
	normalize,
	previewUrl,
	problemsFrom,
	TAB_AI,
	TAB_DESIGN,
	TAB_EMBED,
	TAB_FIELDS,
	TAB_TYPES,
	tabForProblem
} from './definition';
import { Spinner, StatusBadge } from './ui';
import CaseTypesTab from './CaseTypesTab';
import FieldsTab from './FieldsTab';
import AiTab from './AiTab';
import DesignTab from './DesignTab';
import EmbedTab from './EmbedTab';

export const SAVE_DELAY_MS = 2000;
const TOKEN_REFRESH_MS = 9 * 60 * 1000; // preview tokens last 10 minutes

const TABS = [
	{ id: TAB_TYPES, label: I("Case types") },
	{ id: TAB_FIELDS, label: I("Fields") },
	{ id: TAB_AI, label: I("AI") },
	{ id: TAB_DESIGN, label: I("Design") },
	{ id: TAB_EMBED, label: I("Embed") },
];

function SaveIndicator({ state }) {
	if (state === "saving" || state === "pending") {
		return <span className="aif-save"><Spinner /> {I("Saving…")}</span>;
	}
	if (state === "saved") {
		return <span className="aif-save"><i className="fa fa-check" aria-hidden="true" /> {I("Saved")}</span>;
	}
	if (state === "error") {
		return <span className="aif-save error"><i className="fa fa-exclamation-circle" aria-hidden="true" /> {I("Not saved")}</span>;
	}
	return null;
}

export default function AIFormEditor({ formId, onBack }) {
	const [form, setForm] = useState(null);
	const [loadError, setLoadError] = useState("");
	const [areas, setAreas] = useState([]);
	const [embed, setEmbed] = useState(null);
	const [embedError, setEmbedError] = useState("");
	const [draft, setDraft] = useState(null);
	const [name, setName] = useState("");
	const [tab, setTab] = useState(TAB_TYPES);
	const [lang, setLang] = useState("sv");
	const [saveState, setSaveState] = useState("idle");
	const [problems, setProblems] = useState([]);
	const [token, setToken] = useState(null);
	const [nonce, setNonce] = useState(0);
	const [device, setDevice] = useState("desktop");
	const [busy, setBusy] = useState(false);
	const [actionError, setActionError] = useState("");

	const latest = useRef({ name: "", draft: null });
	const dirty = useRef(false);
	const timer = useRef(undefined);
	const chain = useRef(Promise.resolve(true));
	const alive = useRef(true);

	useEffect(() => {
		alive.current = true;
		return () => { alive.current = false; };
	}, []);

	// Start from the server's copy once; afterwards the local copy leads.
	useEffect(() => {
		latest.current = { name: "", draft: null };
		dirty.current = false;
		setForm(null);
		setDraft(null);
		setLoadError("");
		getForm(formId).then(f => {
			if (!alive.current) return;
			const d = normalize(f.draft);
			latest.current = { name: f.name, draft: d };
			setForm(f);
			setDraft(d);
			setName(f.name);
			setLang(d.default_language);
		}).catch(err => alive.current && setLoadError(problemsFrom(err)[0].message));
		listAreas().then(items => alive.current && setAreas(items)).catch(() => undefined);
	}, [formId]);

	const publicKey = form ? form.public_key : "";
	useEffect(() => {
		if (!publicKey) return;
		setEmbedError("");
		getEmbed(formId)
			.then(e => alive.current && setEmbed(e))
			.catch(err => alive.current && setEmbedError(problemsFrom(err)[0].message));
	}, [formId, publicKey]);

	const flush = useCallback(() => {
		window.clearTimeout(timer.current);
		chain.current = chain.current.then(() => {
			if (!dirty.current || !latest.current.draft) {
				return true;
			}
			dirty.current = false;
			if (alive.current) setSaveState("saving");
			return updateForm(formId, { name: latest.current.name, draft: latest.current.draft })
				.then(saved => {
					if (alive.current) {
						if (saved) setForm(saved);
						setProblems([]);
						setSaveState(dirty.current ? "pending" : "saved");
						setNonce(n => n + 1);
					}
					return true;
				})
				.catch(err => {
					dirty.current = true;
					if (alive.current) {
						setProblems(problemsFrom(err));
						setSaveState("error");
					}
					return false;
				});
		});
		return chain.current;
	}, [formId]);

	const schedule = useCallback(() => {
		dirty.current = true;
		setSaveState("pending");
		window.clearTimeout(timer.current);
		timer.current = window.setTimeout(() => { flush(); }, SAVE_DELAY_MS);
	}, [flush]);

	const changeDraft = useCallback((next) => {
		latest.current.draft = next;
		setDraft(next);
		if (next.languages.indexOf(lang) < 0) {
			setLang(next.default_language);
		}
		schedule();
	}, [lang, schedule]);

	const changeName = (value) => {
		latest.current.name = value;
		setName(value);
		if (value.trim()) {
			schedule();
		}
	};

	// Don't lose an unsaved edit when the page is closed or left.
	useEffect(() => {
		const warn = (e) => {
			if (dirty.current) {
				e.preventDefault();
				e.returnValue = "";
			}
		};
		window.addEventListener("beforeunload", warn);
		return () => {
			window.removeEventListener("beforeunload", warn);
			if (dirty.current) {
				flush();
			}
		};
	}, [flush]);

	// A fresh preview token on open and before the old one expires.
	useEffect(() => {
		let live = true;
		const refresh = () => fetchPreviewToken(formId)
			.then(r => live && r && setToken(r.token))
			.catch(() => undefined);
		refresh();
		const id = window.setInterval(refresh, TOKEN_REFRESH_MS);
		return () => {
			live = false;
			window.clearInterval(id);
		};
	}, [formId]);

	const runAction = (fn, after) => {
		setBusy(true);
		setActionError("");
		return fn(formId).then(res => {
			if (!alive.current) return;
			setBusy(false);
			if (res) setForm(res);
			if (after) after(res);
		}).catch(err => {
			if (!alive.current) return;
			setBusy(false);
			const found = problemsFrom(err);
			if (err && err.status === 422) {
				setProblems(found);
				setTab(tabForProblem(found[0] ? found[0].field : ""));
			} else {
				setActionError(found.map(p => p.message).join(" "));
			}
		});
	};

	const publish = () => flush().then(ok => {
		if (!ok) return;
		runAction(publishForm, () => { setProblems([]); setNonce(n => n + 1); });
	});

	const unpublish = () => {
		if (window.confirm(I("Unpublish? Websites using this form show “This form isn't available” until you publish again."))) {
			runAction(unpublishForm);
		}
	};

	const remove = () => {
		if (window.confirm(I("Delete “{name}”? Websites using it stop showing it. Errands already sent stay.").replace("{name}", form.name))) {
			dirty.current = false;
			window.clearTimeout(timer.current);
			runAction(deleteForm, () => onBack());
		}
	};

	const newKey = () => runAction(rotateKey, () => { setEmbed(null); setNonce(n => n + 1); });

	if (loadError) {
		return (
			<div className="aif-page">
				<p className="aif-error">{I("This form couldn't be loaded:")} {loadError}</p>
				<button type="button" className="aif-link" onClick={onBack}>{I("Back to AI forms")}</button>
			</div>
		);
	}
	if (!form || !draft) {
		return <div className="aif-center"><Spinner /></div>;
	}

	const link = (embed && embed.link) || hostedLink(form.public_key);
	const src = previewUrl(link, token, lang, nonce);
	const countFor = t => problems.filter(p => tabForProblem(p.field) === t).length;
	const live = form.status === "live";
	const publishLabel = live ? (form.has_unpublished_changes ? I("Publish changes") : I("Published")) : I("Publish");
	const pending = saveState === "pending" || saveState === "error";

	return (
		<div className="aif-editor">
			<div className="aif-bar">
				<button type="button" className="aif-icon" aria-label={I("Back to AI forms")} title={I("Back to AI forms")} onClick={onBack}>
					<i className="fa fa-arrow-left" aria-hidden="true" />
				</button>
				<input type="text" aria-label={I("Form name")} className="aif-input aif-title" value={name}
					onChange={e => changeName(e.target.value)} />
				<StatusBadge form={form} />
				<SaveIndicator state={saveState} />
				<div className="aif-right">
					{actionError && <span className="aif-error" style={{ marginTop: 0 }}>{actionError}</span>}
					{src && (
						<a className="aif-btn" href={src} target="_blank" rel="noopener noreferrer">
							<i className="fa fa-external-link" aria-hidden="true" /> {I("Preview")}
						</a>
					)}
					{live && (
						<button type="button" className="aif-btn" disabled={busy} onClick={unpublish}>{I("Unpublish")}</button>
					)}
					<button type="button" className="aif-btn primary" onClick={publish}
						disabled={busy || (live && !form.has_unpublished_changes && !pending)}>
						{busy && <Spinner />} {publishLabel}
					</button>
					<button type="button" className="aif-btn danger" aria-label={I("Delete form")} title={I("Delete form")}
						disabled={busy} onClick={remove}>
						<i className="fa fa-trash-o" aria-hidden="true" />
					</button>
				</div>
			</div>

			<div className="aif-body">
				<div className="aif-left">
					<div className="aif-tabs">
						<nav role="tablist" aria-label={I("Form settings")}>
							{TABS.map(t => {
								const count = countFor(t.id);
								return (
									<button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
										className={"aif-tab" + (tab === t.id ? " active" : "")} onClick={() => setTab(t.id)}>
										{t.label}
										{count > 0 && <span className="aif-count">{count}</span>}
									</button>
								);
							})}
						</nav>
						{draft.languages.length > 1 && tab !== TAB_EMBED && tab !== TAB_AI && (
							<label className="aif-lang">
								{I("Texts in")}
								<select value={lang} onChange={e => setLang(e.target.value)}>
									{draft.languages.map(code => <option key={code} value={code}>{LANGUAGE_NAMES[code] || code}</option>)}
								</select>
							</label>
						)}
					</div>

					<div className="aif-scroll">
						{problems.length > 0 && (
							<div className="aif-alert" role="alert" style={{ marginBottom: 14 }}>
								<b>{saveState === "error" ? I("Not saved yet:") : I("Fix this before publishing:")}</b>
								<ul>
									{problems.map((p, i) => (
										<li key={i}>
											<span>{p.message}</span>
											{p.field && (
												<button type="button" className="aif-link" onClick={() => setTab(tabForProblem(p.field))}>{I("Show")}</button>
											)}
										</li>
									))}
								</ul>
							</div>
						)}
						{tab === TAB_TYPES && <CaseTypesTab defn={draft} lang={lang} problems={problems} areas={areas} onChange={changeDraft} />}
						{tab === TAB_FIELDS && <FieldsTab defn={draft} lang={lang} problems={problems} onChange={changeDraft} />}
						{tab === TAB_AI && <AiTab defn={draft} lang={lang} onChange={changeDraft} />}
						{tab === TAB_DESIGN && <DesignTab defn={draft} lang={lang} problems={problems} onChange={changeDraft} />}
						{tab === TAB_EMBED && <EmbedTab form={form} embed={embed} embedError={embedError} busy={busy} onRotateKey={newKey} />}
					</div>
				</div>

				<aside className="aif-right-pane" aria-label={I("Preview")}>
					<div className="aif-previewbar">
						<span>{I("Preview of your latest saved edits")}{live ? "" : " " + I("(the AI answer shows once the form is published)")}</span>
						<div style={{ display: "flex", gap: 2 }}>
							<button type="button" className={"aif-icon" + (device === "desktop" ? " on" : "")} aria-label={I("Desktop width")}
								aria-pressed={device === "desktop"} onClick={() => setDevice("desktop")}>
								<i className="fa fa-desktop" aria-hidden="true" />
							</button>
							<button type="button" className={"aif-icon" + (device === "mobile" ? " on" : "")} aria-label={I("Phone width")}
								aria-pressed={device === "mobile"} onClick={() => setDevice("mobile")}>
								<i className="fa fa-mobile" aria-hidden="true" />
							</button>
							<button type="button" className="aif-icon" aria-label={I("Reload preview")} title={I("Reload preview")}
								onClick={() => setNonce(n => n + 1)}>
								<i className="fa fa-refresh" aria-hidden="true" />
							</button>
						</div>
					</div>
					<div className="aif-previewbox">
						{src ? (
							<iframe key={src} title={I("Form preview")} src={src} className={device === "mobile" ? "mobile" : ""} />
						) : (
							<div className="aif-center"><Spinner /></div>
						)}
					</div>
				</aside>
			</div>
		</div>
	);
}
